import type { LabResult } from "../screens/care/sections/ResultsLabsCard";
import { getBiomarkerFactsheet } from "@src/packages/test-results/content/biomarker-factsheets";
import type { TestResultType } from "@src/packages/test-results/hooks/useTestResults";
import type { ResultInterpretationContext } from "@src/packages/test-results/interpretation-context";
import { resolveResultCategory } from "@src/packages/test-results/result-category";

type DevPreviewLabResult = LabResult & {
  sourceType?: TestResultType | null;
  cycleId?: string | null;
  cycleLabel?: string | null;
};

/**
 * Preview values are placed by the same rule as real results. They carry no
 * lab assessment, so only a threshold with the timing it needs places them.
 */
export function getDevPreviewCategory(
  name: string,
  value: string,
  unit: string,
  context?: ResultInterpretationContext,
): LabResult["category"] {
  return (
    resolveResultCategory({ name, value, unit, context })?.category ?? null
  );
}

const DEV_PREVIEW_LAB_RESULTS_KEY = "__moon_dev_preview_lab_results";
type DevPreviewLabResultsState = "empty" | "filled" | "mixed";

const DEV_PREVIEW_LAB_RESULTS_STATE: DevPreviewLabResultsState = (() => {
  if (!__DEV__ || typeof window === "undefined") return "empty";
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get("dev_preview_lab_results");
    const state =
      value === "mixed"
        ? "mixed"
        : value === "true" || params.get("dev_preview_all_cards") === "true"
          ? "filled"
          : value === "false"
            ? "empty"
            : null;
    if (state) {
      window.sessionStorage?.setItem(DEV_PREVIEW_LAB_RESULTS_KEY, state);
      return state;
    }
    const storedState = window.sessionStorage?.getItem(
      DEV_PREVIEW_LAB_RESULTS_KEY,
    );
    if (
      storedState === "filled" ||
      storedState === "mixed" ||
      storedState === "empty"
    ) {
      return storedState;
    }
    if (value === "false") {
      window.sessionStorage?.removeItem(DEV_PREVIEW_LAB_RESULTS_KEY);
      return "empty";
    }
    return "empty";
  } catch {
    return "empty";
  }
})();

/**
 * The deterministic lab-results preview used by the real product route during
 * local visual review. Keep the AMH entry shared so the AMH factsheet and the
 * lab-results preview exercise the same filled-state data.
 */
export const DEV_PREVIEW_AMH_RESULT: DevPreviewLabResult = {
  id: "dev-preview-amh",
  name: "AMH",
  value: "2.8",
  unit: "ng/mL",
  date: "May 19",
  sourceType: "blood",
  cycleId: "dev-cycle-current",
  cycleLabel: "Current cycle",
  trend: "stable",
  category: getDevPreviewCategory("AMH", "2.8", "ng/mL"),
  history: [
    {
      id: "dev-preview-amh-1",
      date: "Jan 12",
      value: "2.6",
      category: getDevPreviewCategory("AMH", "2.6", "ng/mL"),
    },
    {
      id: "dev-preview-amh-2",
      date: "May 19",
      value: "2.8",
      category: getDevPreviewCategory("AMH", "2.8", "ng/mL"),
    },
  ],
  ...(getBiomarkerFactsheet("AMH") ?? {}),
};

function makePanelPreviewResult({
  id,
  name,
  value,
  previousValue,
  unit,
}: {
  id: string;
  name: string;
  value: string;
  previousValue: string;
  unit: string;
}): DevPreviewLabResult {
  return {
    id,
    name,
    value,
    unit,
    date: "May 19",
    sourceType: "blood",
    cycleId: "dev-cycle-current",
    cycleLabel: "Current cycle",
    trend: "stable",
    category: getDevPreviewCategory(name, value, unit),
    history: [
      {
        id: `${id}-1`,
        date: "Jan 12",
        value: previousValue,
        category: getDevPreviewCategory(name, previousValue, unit),
      },
      {
        id: `${id}-2`,
        date: "May 19",
        value,
        category: getDevPreviewCategory(name, value, unit),
      },
    ],
    ...(getBiomarkerFactsheet(name) ?? {}),
  };
}

/**
 * Filled multi-marker panel preview for local visual review. The entries
 * intentionally share a collection date so the panel reads like one report
 * with several extracted results rather than four unrelated tests.
 */
export const DEV_PREVIEW_PANEL_RESULTS: DevPreviewLabResult[] = [
  DEV_PREVIEW_AMH_RESULT,
  makePanelPreviewResult({
    id: "dev-preview-fsh",
    name: "FSH",
    value: "6.2",
    previousValue: "5.8",
    unit: "mIU/mL",
  }),
  makePanelPreviewResult({
    id: "dev-preview-lh",
    name: "LH",
    value: "4.8",
    previousValue: "5.1",
    unit: "mIU/mL",
  }),
  makePanelPreviewResult({
    id: "dev-preview-estradiol",
    name: "Estradiol",
    value: "62",
    previousValue: "58",
    unit: "pg/mL",
  }),
];

/** Mixed preview: AMH and FSH are present; LH and estradiol are not logged. */
export const DEV_PREVIEW_PANEL_MIXED_RESULTS = DEV_PREVIEW_PANEL_RESULTS.slice(
  0,
  2,
);

export function isDevPreviewLabResultsEnabled(): boolean {
  return DEV_PREVIEW_LAB_RESULTS_STATE !== "empty";
}

export function getDevPreviewLabResultsState(): DevPreviewLabResultsState {
  return DEV_PREVIEW_LAB_RESULTS_STATE;
}
